/**
 * The virtual account's Change log tab (TMMAPP-0022, AUD-03; TMMAPP-0112, MM-D-119): every member
 * reads it. A toolbar — the Kinds segmented control (All · Money · Budgets · Envelopes · Categories ·
 * Plans · Meetings · Members, the server's `kinds`), a Person select (the owner and the active
 * members, the server's `actorUserId`) and From / To — over the audit table in 0103's style. Renders
 * exactly the API's protected rows, never the owner view; account ids are text, not links (MM-D-43);
 * amounts use the virtual account owner's currency. Below `md` the kinds are a select.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { auditApi, CHANGE_LOG_KINDS, type AuditEvent, type ChangeLogKind } from '@/api/audit';
import type { VirtualAccountDetail } from '@/api/virtualAccounts';
import { exportsApi } from '@/api/exports';
import { ClearFiltersButton } from '@/components/ClearFiltersButton';
import { ErrorState } from '@/components/ErrorState';
import { SegmentedControl } from '@/components/SegmentedControl';
import { AuditList, type NameMaps } from '@/features/audit/AuditList';
import { defaultAuditRange, isCivilDate, validRange } from '@/features/audit/dates';
import { useLocale } from '@/i18n/useLocale';
import { personName } from '@/i18n/personName';
import { useMe } from '@/session/meHooks';
import { useNotify } from '@/shell/NotifyProvider';
import {
  activeCount,
  civilDateParam,
  defaultsOf,
  intParam,
  stringParam,
  useUrlState,
} from '@/shared/useUrlState';
import { downloadBlob } from '@/utils/downloadBlob';

const SKELETON_KEYS = ['a', 'b', 'c'] as const;
const FILTERS = ['af', 'at', 'ak', 'ap'] as const;
type KindChoice = ChangeLogKind | 'ALL';

type PageState =
  | { key: string; status: 'ready'; rows: AuditEvent[]; nextCursor: string | null }
  | { key: string; status: 'error'; error: unknown };

interface ActivityTabProps {
  virtualAccount: VirtualAccountDetail;
}

/** The people who can act on the virtual account: its owner, then each active member with an id. */
function membersOf(
  virtualAccount: VirtualAccountDetail,
): { userId: number; name: string | null }[] {
  const seen = new Set<number>([virtualAccount.ownerUserId]);
  const members = [{ userId: virtualAccount.ownerUserId, name: virtualAccount.ownerDisplayName }];
  for (const share of virtualAccount.shares) {
    if (share.status !== 'ACTIVE' || share.userId === null || seen.has(share.userId)) continue;
    seen.add(share.userId);
    members.push({ userId: share.userId, name: share.displayName });
  }
  return members;
}

export function ActivityTab({ virtualAccount }: ActivityTabProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const theme = useTheme();
  const md = useMediaQuery(theme.breakpoints.up('md'));
  const me = useMe();
  const zone = me.user.timezone ?? 'UTC';
  // `af` / `at`: the range, omitted while it is the last 30 days; `ak`: the kind; `ap`: the person.
  // The Register tab owns `from` / `to`.
  const schema = useMemo(() => {
    const range = defaultAuditRange(zone);
    const options = { allowEmpty: true };
    return {
      af: civilDateParam(range.from, options),
      at: civilDateParam(range.to, options),
      ak: stringParam(),
      ap: intParam(0, { min: 0 }),
    };
  }, [zone]);
  const [urlState, setUrl] = useUrlState(schema);
  const { af: from, at: to } = urlState;
  const kind: KindChoice = (CHANGE_LOG_KINDS as readonly string[]).includes(urlState.ak)
    ? (urlState.ak as ChangeLogKind)
    : 'ALL';
  const members = membersOf(virtualAccount);
  const actorUserId = members.some((m) => m.userId === urlState.ap) ? urlState.ap : 0;
  const [tick, setTick] = useState(0);
  const [page, setPage] = useState<PageState | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const notify = useNotify();
  const [exporting, setExporting] = useState(false);
  const lastKey = useRef<string | null>(null);
  const seq = useRef(0);

  const rangeOk = validRange(from, to);
  const key = rangeOk ? `${virtualAccount.id}|${from}|${to}|${kind}|${actorUserId}|${tick}` : null;

  const query = useCallback(
    (cursor?: string) =>
      auditApi.listVirtualAccount(virtualAccount.id, {
        from,
        to,
        timeZone: zone,
        ...(kind !== 'ALL' ? { kinds: [kind] } : {}),
        ...(actorUserId > 0 ? { actorUserId } : {}),
        ...(cursor !== undefined ? { cursor } : {}),
      }),
    [virtualAccount.id, from, to, zone, kind, actorUserId],
  );

  const load = useCallback(async () => {
    if (key === null) return;
    const mine = ++seq.current;
    try {
      const result = await query();
      if (seq.current !== mine) return;
      setPage({ key, status: 'ready', rows: result.items, nextCursor: result.nextCursor });
    } catch (error) {
      if (seq.current !== mine) return;
      setPage({ key, status: 'error', error });
    }
  }, [key, query]);

  useEffect(() => {
    if (key === null || lastKey.current === key) return;
    lastKey.current = key;
    void load();
  }, [key, load]);

  const ready = page !== null && page.key === key && page.status === 'ready' ? page : null;
  const failed = page !== null && page.key === key && page.status === 'error' ? page : null;
  const loading = key !== null && ready === null && failed === null;

  async function handleLoadMore() {
    if (ready === null || ready.nextCursor === null || loadingMore) return;
    const startKey = ready.key;
    setLoadingMore(true);
    try {
      const next = await query(ready.nextCursor);
      setPage((prev) =>
        prev !== null && prev.status === 'ready' && prev.key === startKey
          ? { ...prev, rows: [...prev.rows, ...next.items], nextCursor: next.nextCursor }
          : prev,
      );
    } finally {
      setLoadingMore(false);
    }
  }

  async function handleExport() {
    if (!rangeOk || exporting) return;
    setExporting(true);
    try {
      const { blob, filename } = await exportsApi.virtualAccountAuditCsv(virtualAccount.id, {
        from,
        to,
        timeZone: zone,
      });
      downloadBlob(blob, filename ?? 'audit.csv');
      notify(t('exports.ready'));
    } catch {
      notify(t('exports.failed'), { severity: 'error' });
    } finally {
      setExporting(false);
    }
  }

  const names: NameMaps = {
    virtualAccounts: { [virtualAccount.id]: virtualAccount.name },
    accounts: {},
    envelopes: Object.fromEntries(
      virtualAccount.envelopes.map((envelope) => [envelope.id, envelope.name]),
    ),
  };
  const ctx = {
    locale,
    timeZone: zone,
    currency: virtualAccount.currency,
  };
  const kindOptions = [
    { value: 'ALL' as const, label: t('changeLog.kind.ALL') },
    ...CHANGE_LOG_KINDS.map((value) => ({ value, label: t(`changeLog.kind.${value}`) })),
  ];
  const setKind = (next: KindChoice) => setUrl({ ak: next === 'ALL' ? '' : next });

  return (
    <Box>
      <Stack sx={{ gap: 2, mb: 2 }}>
        {md ? (
          <Box sx={{ overflowX: 'auto' }}>
            <SegmentedControl<KindChoice>
              aria-label={t('changeLog.kindsLabel')}
              value={kind}
              filter
              options={kindOptions}
              onChange={setKind}
            />
          </Box>
        ) : (
          <FormControl size="small" fullWidth>
            <InputLabel id="change-log-kind">{t('changeLog.kindsLabel')}</InputLabel>
            <Select
              labelId="change-log-kind"
              label={t('changeLog.kindsLabel')}
              value={kind}
              onChange={(event) => setKind(event.target.value as KindChoice)}
            >
              {kindOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          sx={{ alignItems: { md: 'flex-end' }, flexWrap: 'wrap', gap: 2 }}
        >
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel id="change-log-person" shrink>
              {t('changeLog.person')}
            </InputLabel>
            <Select
              labelId="change-log-person"
              label={t('changeLog.person')}
              notched
              displayEmpty
              value={String(actorUserId)}
              onChange={(event) => setUrl({ ap: Number(event.target.value) })}
            >
              <MenuItem value="0">{t('changeLog.anyone')}</MenuItem>
              {members.map((member) => (
                <MenuItem key={member.userId} value={String(member.userId)}>
                  {member.userId === me.user.id
                    ? t('audit.actor.USER')
                    : personName(member.name, t)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            type="date"
            size="small"
            label={t('audit.from')}
            value={from}
            onChange={(event) => {
              const next = event.target.value;
              if (isCivilDate(next) || next === '') setUrl({ af: next });
            }}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            type="date"
            size="small"
            label={t('audit.to')}
            value={to}
            onChange={(event) => {
              const next = event.target.value;
              if (isCivilDate(next) || next === '') setUrl({ at: next });
            }}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <ClearFiltersButton
            active={activeCount(schema, urlState, FILTERS)}
            onClear={() => setUrl(defaultsOf(schema))}
          />
          <Tooltip title={t('audit.exportRangeOnly')} describeChild>
            <span>
              <Button
                variant="outlined"
                onClick={() => void handleExport()}
                disabled={!rangeOk || exporting}
              >
                {t('audit.exportCsv')}
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </Stack>

      {failed !== null && (
        <ErrorState
          variant="section"
          error={failed.error}
          what={t('errors.what.activity')}
          onRetry={() => {
            lastKey.current = null;
            setTick((n) => n + 1);
          }}
        />
      )}

      {loading && (
        <Stack spacing={1}>
          {SKELETON_KEYS.map((k) => (
            <Skeleton key={k} variant="rounded" height={48} />
          ))}
        </Stack>
      )}

      {ready !== null && (
        <AuditList
          rows={ready.rows}
          nextCursor={ready.nextCursor}
          loadingMore={loadingMore}
          onLoadMore={() => void handleLoadMore()}
          meUserId={me.user.id}
          mode="protected"
          currentVirtualAccountId={virtualAccount.id}
          names={names}
          ctx={ctx}
          {...(kind !== 'ALL' || actorUserId > 0
            ? { emptyText: t('changeLog.emptyFiltered') }
            : {})}
        />
      )}
    </Box>
  );
}
